import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { showSuccess, showError } from "@/lib/toastHelpers";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

export default function HealthTab() {
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSubject, setTestSubject] = useState("");
  const [testHtml, setTestHtml] = useState("<p>Hello {{firstName}},</p><p>This is a test email.</p>");
  const [sending, setSending] = useState(false);
  const qc = useQueryClient();

  const { data: health, isLoading: healthLoading, refetch } = useQuery({
    queryKey: ["/api/admin/email-queue/health"],
    queryFn: () => apiRequest("GET", "/api/admin/email-queue/health") as unknown as Promise<any>,
    refetchInterval: 60_000,
  });

  const { data: dailyStats = [], isLoading: statsLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/email-queue/stats/daily"],
    queryFn: () => apiRequest("GET", "/api/admin/email-queue/stats/daily?days=7") as unknown as Promise<any[]>,
    staleTime: 5 * 60_000,
  });

  async function sendTest() {
    if (!testEmail.trim()) return showError("To address required");
    if (!testSubject.trim()) return showError("Subject required");
    setSending(true);
    try {
      const r: any = await apiRequest("POST", "/api/admin/email-queue/test", {
        toEmail: testEmail.trim(),
        subject: testSubject.trim(),
        templateHtml: testHtml,
      });
      showSuccess(r.skipped ? "Skipped (suppressed address)" : `Test sent — messageId: ${r.messageId}`);
      setTestOpen(false);
    } catch (e: any) {
      showError(e.message || "Send failed");
    } finally {
      setSending(false);
    }
  }

  // Prepare chart data — newest-last, 7 days
  const chartData = [...dailyStats].reverse().map(d => ({
    date:   d._id,
    Sent:   d.sent?.total ?? 0,
    Failed: d.failed ?? 0,
    Opens:  d.opened ?? 0,
    Bounces: d.bounced ?? 0,
  }));

  const cb = health?.bounceCircuitBreaker;
  const cbTripped = cb?.tripped;

  return (
    <div className="space-y-6">
      {/* Health status */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">
              {healthLoading ? "—" : (health?.blastQueue?.pending ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">Pending in queue</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-purple-600">
              {healthLoading ? "—" : (health?.blastQueue?.processing ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">Processing now</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-orange-500">
              {healthLoading ? "—" : (health?.sequenceBacklog ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">Sequence backlog</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-600">
              {healthLoading ? "—" : (health?.suppression?.total ?? 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total suppressed</div>
          </CardContent>
        </Card>
      </div>

      {/* Bounce circuit breaker */}
      {health && cb && (
        <Card className={cbTripped ? "border-red-300 bg-red-50" : "border-green-200 bg-green-50/40"}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className={`font-semibold text-sm ${cbTripped ? "text-red-700" : "text-green-700"}`}>
                  {cbTripped ? "⚠️ Bounce circuit breaker OPEN — bulk sends paused" : "✅ Bounce circuit breaker closed"}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Last hour: {cb.bouncedLast1h} bounces / {cb.sentLast1h} sent
                  {cb.bounceRate !== null && ` (${(cb.bounceRate * 100).toFixed(1)}% — threshold ${(cb.threshold * 100).toFixed(0)}%)`}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => refetch()}>Refresh</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's quota */}
      {health?.quota && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Today's Quota</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              {["otp", "transactional", "bulk"].map(lane => {
                const sent  = (health.quota[lane] as number) ?? 0;
                const limit = (health.quota.dailyLimit as number) ?? 0;
                const pct   = limit > 0 ? Math.min(100, Math.round((sent / limit) * 100)) : 0;
                return (
                  <div key={lane} className="space-y-1">
                    <div className="text-xs text-muted-foreground capitalize">{lane}</div>
                    <div className="text-lg font-semibold">{sent.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">sent / {limit.toLocaleString()}</div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 7-day chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Last 7 Days</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {statsLoading ? (
            <div className="text-sm text-muted-foreground">Loading stats…</div>
          ) : chartData.length === 0 ? (
            <div className="text-sm text-muted-foreground">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Sent"    fill="#3b82f6" radius={[2,2,0,0]} />
                <Bar dataKey="Opens"   fill="#a855f7" radius={[2,2,0,0]} />
                <Bar dataKey="Bounces" fill="#ef4444" radius={[2,2,0,0]} />
                <Bar dataKey="Failed"  fill="#f97316" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Test send */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Test Send</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setTestOpen(o => !o)}>
              {testOpen ? "Cancel" : "Send a test"}
            </Button>
          </div>
        </CardHeader>
        {testOpen && (
          <CardContent className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">To address</Label>
                <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" className="h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subject</Label>
                <Input value={testSubject} onChange={e => setTestSubject(e.target.value)} placeholder="Test subject" className="h-8 text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">HTML body (supports {"{{firstName}}"} and {"{{email}}"})</Label>
              <Textarea value={testHtml} onChange={e => setTestHtml(e.target.value)} rows={4} className="text-sm font-mono" />
            </div>
            <Button size="sm" onClick={sendTest} disabled={sending}>
              {sending ? "Sending…" : "Send test email"}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
